import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getDashboards } from '../services/dashboardService'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export default function Sidebar({ isOpen, onClose }: Readonly<SidebarProps>) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const location = useLocation()
  const dashboards = getDashboards()

  // Close mobile drawer on navigation
  useEffect(() => {
    onClose()
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <aside
      className={[
        'flex flex-col flex-shrink-0 bg-cbc-navy/95 backdrop-blur-xl border-r border-cbc-medium-blue/15',
        'fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-in-out',
        'md:relative md:inset-auto md:z-auto md:translate-x-0',
        isOpen ? 'translate-x-0' : '-translate-x-full',
        isCollapsed ? 'md:w-14' : 'md:w-56',
        'w-56',
      ].join(' ')}
    >
      {/* Header: X on mobile / collapse on desktop */}
      <div className="flex items-center px-3 py-3 border-b border-cbc-medium-blue/15">
        <button
          onClick={onClose}
          className="md:hidden p-1.5 rounded-md text-cbc-medium-blue/70 hover:text-cbc-light-blue hover:bg-cbc-light-blue/10 transition-all duration-200"
          title="Cerrar menú"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <span className="flex-1 md:hidden" />
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden md:block p-1.5 rounded-md text-cbc-medium-blue/70 hover:text-cbc-light-blue hover:bg-cbc-light-blue/10 transition-all duration-200 ml-auto"
          title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {/* Home link */}
        {(() => {
          const active = location.pathname === '/'
          return (
            <Link
              to="/"
              className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 border-l-2 ${
                active
                  ? 'border-cbc-primary bg-gradient-to-r from-cbc-primary/20 via-cbc-dark-blue to-transparent text-cbc-paper shadow-cbc-dark'
                  : 'border-transparent text-cbc-medium-blue/80 hover:text-cbc-light-blue hover:bg-cbc-light-blue/[0.08]'
              }`}
            >
              <svg
                className={`w-4 h-4 flex-shrink-0 transition-colors ${active ? 'text-cbc-light-blue' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {!isCollapsed && <span className="text-sm font-medium">Inicio</span>}
            </Link>
          )
        })()}

        <div className="pt-4">
          {!isCollapsed && (
            <p className="px-3 mb-2 text-[10px] font-semibold text-cbc-medium-blue/55 uppercase tracking-widest">
              Dashboards
            </p>
          )}
          {dashboards.map((dashboard) => {
            const active = location.pathname === `/dashboard/${dashboard.id}`
            return (
              <Link
                key={dashboard.id}
                to={`/dashboard/${dashboard.id}`}
                className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 border-l-2 ${
                  active
                    ? 'border-cbc-primary bg-gradient-to-r from-cbc-primary/20 via-cbc-dark-blue to-transparent text-cbc-paper shadow-cbc-dark'
                    : 'border-transparent text-cbc-medium-blue/80 hover:text-cbc-light-blue hover:bg-cbc-light-blue/[0.08]'
                }`}
                title={isCollapsed ? dashboard.name : ''}
              >
                <svg
                  className={`w-4 h-4 flex-shrink-0 transition-colors ${active ? 'text-cbc-light-blue' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {!isCollapsed && <span className="text-sm font-medium">{dashboard.name}</span>}
              </Link>
            )
          })}
        </div>
      </nav>
    </aside>
  )
}
